import moment from 'moment'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import InfiniteScroll from '../../infinite-scroll/InfiniteScroll'

import { ReactComponent as OutsideLinkIcon } from '../../../assets/images/common/icons/outside-link-icon.svg'
import { ReactComponent as SortIcon } from '../../../assets/images/common/icons/sort-icon.svg'
import { ReactComponent as BillingXCircle } from '../../../assets/images/common/icons/billing-x-circle.svg'
import { ReactComponent as BillingCheckCircle } from '../../../assets/images/common/icons/billing-check-circle.svg'
import { ReactComponent as BillingRefundCircle } from '../../../assets/images/common/icons/billing-refund-circle.svg'
import { ReactComponent as BillingPendingCircle } from '../../../assets/images/common/icons/stripe-pending-icon.svg'
import ProfileAvatar from '../../common/avatar/ProfileAvatar'
import Loader from '../../common/loader/Loader'
import LoadingSpinner from '../../common/loader/legacy-loaders/LoadingSpinner'

import classes from './EnrollmentsBillingTable.module.scss'
import colors from '../../../assets/styles/globals.scss'

import { ENROLLMENTS_COLUMNS, getSortStates, ENROLLMENT_STATUS_LABELS, formatPaymentStatusDisplay, ACTIVITY_LABELS } from '../../../utility/enrollmentsBilling'
import { displayWithCurrency } from '../../../utility/currency'
import { genderLabel } from '../../../utility/userHelper'
import { useSelector } from 'react-redux'


const BILLING_STATUS_ICON = {
  outstanding: <BillingXCircle className={classes.iconOutstanding} width={19} height={19} />,
  invoiced: <BillingPendingCircle className={classes.iconInvoiced} width={19} height={19} />,
  paid: <BillingCheckCircle className={classes.iconPaid} width={19} height={19} />,
  offlinePayment: <BillingCheckCircle className={classes.iconOfflinePayment} width={19} height={19} />,
  refunded: <BillingRefundCircle className={classes.iconRefunded} width={19} height={19} />,
  none: <BillingXCircle className={classes.iconNone} width={19} height={19} />,
}

const SortButton = ({ label, active, onClick, loading }) => (
  <button className={classNames(classes.sortButton, { [classes.active]: active })} onClick={onClick} disabled={loading}>
    {label}
    {loading ? (
      <LoadingSpinner classNames={{ wrapper: classes.spinnerWrapper }} />
    ) : (
      <SortIcon className={classes.sortIcon} width={16} height={16} color={colors.slateGrayIcons} />
    )}
  </button>
)

SortButton.propTypes = {
  label: PropTypes.string,
  active: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
}

const formatProgramDates = (course) => {
  if (!course?.startDate) { return '-' }

  const start = moment(course.startDate).format('MM/DD/YYYY')
  const end = course.endDate ? ` - ${moment(course.endDate).format('MM/DD/YYYY')}` : ''
  return `${start}${end}`
}

const LatestUpdate = ({ enrollment }) => {
  const { latestActivity } = enrollment
  if (!latestActivity) { return <span>-</span> }

  const label = ACTIVITY_LABELS[latestActivity.changeType] || latestActivity.changeType
  const date = latestActivity.createdAt ? moment(latestActivity.createdAt).format('MM/DD/YYYY') : ''
  return (
    <div className={classes.latestUpdate}>
      <p className={classes.latestUpdateLabel}>{label}</p>
      <p className={classes.latestUpdateDate}>{date}</p>
    </div>
  )
}

LatestUpdate.propTypes = {
  enrollment: PropTypes.shape({
    latestActivity: PropTypes.shape({
      changeType: PropTypes.string,
      createdAt: PropTypes.string,
    }),
  }).isRequired,
}

// eslint-disable-next-line max-lines-per-function
const EnrollmentsBillingTable = ({
  enrollments, fetchData, pagination, onRowClick, selectedEnrollmentId,
  isExporting, isLoading, isSortLoading, sortOption, applySort,
  onApprove, onDecline,
}) => {
  const hasMore = !pagination?.totalPages || (pagination?.page + 1) <= pagination?.totalPages
  const currency = useSelector(state => state.user.current_user.communities[0].currency)

  if (!enrollments || !pagination) {
    return (
      <div className='h-100 d-flex align-items-center justify-content-center'>
        <Loader>Loading Enrollments</Loader>
      </div>
    )
  }

  const sortStates = getSortStates()

  const getNextSortValue = (selectedSortField, currentSortValue) => {
    if (selectedSortField !== sortOption.sortField) {
      return sortStates.at(0)
    }
    if (currentSortValue === sortStates.at(-1)) {
      return null
    }
    const currentIndex = sortStates.indexOf(currentSortValue)
    return sortStates[(currentIndex + 1) % sortStates.length]
  }

  return (
    <div id='enrollmentsBillingTable' className={classes.table}>
      <InfiniteScroll
        dataLength={enrollments.length}
        next={fetchData}
        hasMore={hasMore}
        scrollableTarget='enrollmentsTableContent'
        className={classes.tableContent}
        hideLoader
      >
        <div className={classes.gridTable} id='enrollmentsTableContent'>
          {/* Fixed first column header: Student */}
          <div className={classNames(classes.col, classes.header, classes.firstColumn, classes.topLeft)}>
            <span className='font-weight-bold'>Enrollments ({pagination?.totalCount})</span>
          </div>

          {/* Column headers (sortable unless flagged otherwise) */}
          {ENROLLMENTS_COLUMNS.map(({ label, field, sortable }, idx) => (
            <div
              className={classNames(classes.col, classes.header, { [classes.headerProgram]: field === 'program_name' })}
              key={idx}
            >
              {sortable === false ? (
                <span className={classes.headerLabel}>{label}</span>
              ) : (
                <SortButton
                  label={label}
                  active={sortOption.sortField === field && sortOption.sortValue !== null}
                  onClick={() => applySort(field, getNextSortValue(field, sortOption.sortValue))}
                  loading={isSortLoading && sortOption.sortField === field}
                />
              )}
            </div>
          ))}

          {isExporting ? (
            <div className={classes.exportLoadingWrapper}>
              <Loader loaderType='ball-beat' loaderColor={colors.lightNavy}>Exporting Enrollments</Loader>
              <span className='text-center'>Depending on data size this may take several minutes.</span>
            </div>
          ) : (
            <>
              {enrollments.map(enrollment => {
                const { student, course } = enrollment
                const displayStatus = enrollment.displayStatus
                const billingDisplay = formatPaymentStatusDisplay(enrollment.paymentStatus)

                return (
                  <div
                    tabIndex={0}
                    key={enrollment.id}
                    role='button'
                    className={classNames(classes.row, { [classes.isClicked]: enrollment.id === selectedEnrollmentId })}
                    onClick={() => onRowClick(enrollment)}
                    onKeyDown={() => null}
                  >
                    {/* Student (fixed first column) */}
                    <div
                      className={classNames(classes.col, classes.firstColumn, classes.studentInfo)}
                      title={`${student.firstName} ${student.lastName}`}
                    >
                      <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
                      <div className='d-flex flex-column flex-grow-1 pr-3'>
                        <h6 className='mb-1 font-weight-bold'>{student.firstName} {student.lastName}</h6>
                        <span>
                          {student.age !== null && `${student.age}yrs`}
                          {student.age !== null && student.gender !== null && '/'}
                          {genderLabel[student.gender]}
                        </span>
                      </div>
                      <OutsideLinkIcon className={classes.detailsIcon} />
                    </div>

                    {/* Program */}
                    <div className={classNames(classes.col, classes.programCol)}>
                      <div className={classes.programCell}>
                        <span className={classes.programName} title={course?.name}>
                          {course?.name || '-'}
                        </span>
                        {course?.meetingTimes && (
                          <span className={classes.programMeetingTimes}>{course.meetingTimes}</span>
                        )}
                      </div>
                    </div>

                    {/* Enrollment Status */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      {displayStatus === 'pending_approval' ? (
                        <div className={classes.approvalActions}>
                          <button
                            className={classes.approveButton}
                            onClick={(e) => { e.stopPropagation(); onApprove?.(enrollment) }}
                          >
                            Approve
                          </button>
                          <button
                            className={classes.declineButton}
                            onClick={(e) => { e.stopPropagation(); onDecline?.(enrollment) }}
                          >
                            Decline
                          </button>
                        </div>
                      ) : (
                        <span className={classNames(
                          classes.statusBadge,
                          {
                            [classes.statusActive]: displayStatus === 'active',
                            [classes.statusFuture]: displayStatus === 'future',
                            [classes.statusCompleted]: displayStatus === 'completed',
                            [classes.statusDropped]: displayStatus === 'dropped',
                            [classes.statusWaitlisted]: displayStatus === 'waitlisted',
                          }
                        )}>
                          {ENROLLMENT_STATUS_LABELS[displayStatus] || displayStatus}
                        </span>
                      )}
                    </div>

                    {/* Billing Status (phase 1: enrollment payment status) */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span className={classNames(classes.invoiceBadge, classes[`invoice_${billingDisplay.variant}`])}>
                        {BILLING_STATUS_ICON[billingDisplay.variant]}
                        {billingDisplay.label}
                      </span>
                    </div>

                    {/* Latest Update */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <LatestUpdate enrollment={enrollment} />
                    </div>

                    {/* Amount (phase 1: enrollment amount) */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span className='font-weight-bold'>
                        {enrollment.amount !== null ? displayWithCurrency(enrollment.amount, currency, true) : '-'}
                      </span>
                    </div>

                    {/* Payment Method (phase 1: enrollment payment method — card = online, cash = offline) */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{enrollment.paymentMethod === 'card' ? 'Online' : enrollment.paymentMethod ? 'Offline' : '-'}</span>
                    </div>

                    {/* Package */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span className='text-truncate' title={enrollment.package?.name}>
                        {enrollment.package?.name || '-'}
                      </span>
                    </div>

                    {/* Age */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{student.age ?? '-'}</span>
                    </div>

                    {/* Level */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{student.level !== null ? `L${student.level}` : '-'}</span>
                    </div>

                    {/* Gender */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{genderLabel[student.gender] || '-'}</span>
                    </div>

                    {/* Program Dates */}
                    <div className={classNames(classes.col, classes.centeredCol)}>
                      <span>{formatProgramDates(course)}</span>
                    </div>
                  </div>
                )
              })}

              {/* Skeleton loading rows */}
              {!!(hasMore && isLoading) && Array.from({ length: 3 }, (_, idx) => (
                <div className={classNames(classes.row, classes.skeletons)} key={`skeleton-${idx}`}>
                  <div className={classNames(classes.col, classes.firstColumn, classes.studentInfo)}>
                    <div data-skeleton='avatar' />
                    <div className={classNames('d-flex flex-column flex-grow-1 pr-3', classes.skeletonInfoWrapper)}>
                      <div data-skeleton='name' />
                      <div data-skeleton='info' />
                    </div>
                  </div>
                  {ENROLLMENTS_COLUMNS.map((_, colIdx) => (
                    <div className={classNames(classes.col, classes.centeredCol)} key={colIdx}>
                      <div data-skeleton='longValue' />
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

        </div>
      </InfiniteScroll>
    </div>
  )
}

EnrollmentsBillingTable.propTypes = {
  enrollments: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  onApprove: PropTypes.func,
  onDecline: PropTypes.func,
  pagination: PropTypes.object,
  selectedEnrollmentId: PropTypes.number,
  isExporting: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSortLoading: PropTypes.bool,
  sortOption: PropTypes.object,
  applySort: PropTypes.func,
}

export default EnrollmentsBillingTable
